export function newWorksheetId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `id-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export function speakerDisplayName(item) {
  if (!item) return "";
  if (item.role === "Spiritual Director") {
    return [item.title, item.firstName, item.lastName]
      .map((part) => (part || "").trim())
      .filter(Boolean)
      .join(" ");
  }

  const hisFirst = (item.hisFirstName || "").trim();
  const herFirst = (item.herFirstName || "").trim();
  const hisLast = (item.hisLastName || "").trim();
  const herLast = (item.herLastName || "").trim();
  const last = hisLast || herLast;

  if (item.role === "Spiritual Mentor") {
    if (hisFirst && herFirst) {
      return `${hisFirst} & ${herFirst}${last ? ` ${last}` : ""}`;
    }
    if (hisFirst) return `${hisFirst}${hisLast ? ` ${hisLast}` : ""}`.trim();
    if (herFirst) return `${herFirst}${herLast ? ` ${herLast}` : ""}`.trim();
    return last;
  }

  const firsts = [hisFirst, herFirst].filter(Boolean).join(" & ");
  return [firsts, last].filter(Boolean).join(" ");
}

export function talkHeading(item, { label = "Talk", index = 0, isSpanish = false } = {}) {
  const name = isSpanish ? item?.spanishTalk || item?.talk : item?.talk;
  return `${label} ${index + 1}: ${name || ""}`.trim();
}

function cleanList(list, mapRow) {
  if (!Array.isArray(list)) return [];
  return list.map(mapRow).filter(Boolean);
}

function escapeHtml(value) {
  return String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

export function structuredWorksheetToHtml(parsed) {
  const verses = (parsed?.verses || [])
    .map((verse) => {
      const quote = `<p style="text-align: center;"><span style="color: #1e3a6e;"><em>${escapeHtml(
        formatVerseText(verse.text),
      )}</em></span></p>`;
      const citation = verse.citation
        ? `<p style="text-align: center;"><span style="color: #1e3a6e;"><em>${escapeHtml(
            formatCitation(verse.citation),
          )}</em></span></p>`
        : "";
      return `${quote}${citation}`;
    })
    .join("");

  const questions = (parsed?.questions || [])
    .map(
      (question, index) =>
        `<p><span style="color: #161616;">${index + 1}. ${escapeHtml(
          question.text,
        ).replace(/\n/g, "<br>")}</span></p>`,
    )
    .join("");

  return `${verses}${questions}`;
}

export function parseTalkWorksheet(raw) {
  if (raw == null) return null;
  const text = String(raw).trim();
  if (!text.startsWith("{")) return null;

  try {
    const data = JSON.parse(text);
    if (!data || data.format !== "worksheet") return null;

    if (typeof data.html === "string") {
      return { format: "worksheet", html: data.html };
    }

    if (!Array.isArray(data.verses) || !Array.isArray(data.questions)) {
      return null;
    }

    const verses = cleanList(data.verses, (verse) => {
      const verseText = String(verse?.text || "").trim();
      if (!verseText) return null;
      return {
        id: String(verse.id || newWorksheetId()),
        text: verseText,
        citation: String(verse?.citation || "").trim(),
      };
    });
    const questions = cleanList(data.questions, (question) => {
      const questionText = String(question?.text || "").trim();
      if (!questionText) return null;
      return {
        id: String(question.id || newWorksheetId()),
        text: questionText,
      };
    });

    return { format: "worksheet", verses, questions };
  } catch (error) {
    return null;
  }
}

export function isLegacyTalkHtml(raw) {
  const text = raw == null ? "" : String(raw).trim();
  return text.length > 0 && parseTalkWorksheet(text) == null;
}

export function editorHtmlFromStored(raw) {
  const parsed = parseTalkWorksheet(raw);
  if (!parsed) return isLegacyTalkHtml(raw) ? String(raw) : "";
  if (typeof parsed.html === "string") return parsed.html;
  return structuredWorksheetToHtml(parsed);
}

export function normalizeEditorHtml(html) {
  return String(html || "")
    .replace(/&nbsp;/gi, " ")
    .replace(/<p><br\s*\/?><\/p>/gi, "")
    .replace(/<[^>]*>/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

export function editorSignature(html) {
  const compact = String(html || "")
    .replace(/\s+/g, " ")
    .trim();
  if (!normalizeEditorHtml(compact)) return "";
  return compact;
}

export function serializeTalkWorksheet(html) {
  return JSON.stringify({
    format: "worksheet",
    html: normalizeEditorHtml(html) ? String(html || "") : "",
  });
}

export function worksheetHasRules(html) {
  return /<hr\b/i.test(String(html || ""));
}

export function worksheetLineCount(verseCount = 0, questionCount = 0) {
  const used = 90 + verseCount * 72 + questionCount * 56;
  const remaining = Math.max(0, 920 - used);
  return Math.max(5, Math.min(16, Math.floor(remaining / 32)));
}

export function worksheetLineCountFromHtml(html) {
  if (worksheetHasRules(html)) return 0;
  const text = normalizeEditorHtml(html);
  const weight = Math.ceil(text.length / 90);
  const used = 90 + weight * 36;
  const remaining = Math.max(0, 920 - used);
  return Math.max(5, Math.min(16, Math.floor(remaining / 32)));
}

export function formatVerseText(text) {
  const trimmed = String(text || "").trim();
  if (!trimmed) return "";
  if (/^["“]/.test(trimmed) && /["”]$/.test(trimmed)) return trimmed;
  return `\u201c${trimmed}\u201d`;
}

export function formatCitation(citation) {
  const trimmed = String(citation || "").trim();
  if (!trimmed) return "";
  if (trimmed.startsWith("–") || trimmed.startsWith("-")) return trimmed;
  return `\u2013 ${trimmed} \u2013`;
}
